import { useEffect, useState } from 'react'
import { ScanLine, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { typedDoc, getDocument } from '@/lib/firebase/firestore'
import { adminEventsService } from '../services/admin.events.service'
import { useCheckIn } from '../hooks/useCheckIn'
import { QrScanner } from '../components/QrScanner'
import { CheckInResult } from '../components/CheckInResult'
import type { EventDocument, TicketDocument, UserDocument } from '@/types'

interface TicketCount {
  ticketTypeId: string
  ticketTypeName: string
  total: number
  checkedIn: number
}

export function CheckInPage() {
  const { user } = useAuth()
  const [events, setEvents] = useState<Array<EventDocument & { id: string }>>([])
  const [eventsLoading, setEventsLoading] = useState(true)
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
  const [ticketCounts, setTicketCounts] = useState<TicketCount[]>([])

  const { state, result, errorMsg, checkIn, reset, startScanning } = useCheckIn()

  // Determine if user is admin (role comes from Firestore, not Firebase Auth)
  // We load their user doc to check
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    if (!user) return
    // Load user profile from Firestore to get role + assignedEvents
    getDocument(typedDoc<UserDocument>('users', user.uid))
      .then((userData) => {
        if (!userData) return
        const u = userData
        if (u.role === 'admin') {
            setIsAdmin(true)
            // Admin sees all events
            adminEventsService.getAllEvents().then((all) => {
              setEvents(all)
              setEventsLoading(false)
            })
          } else {
            // Staff only sees assigned events
            const assigned = u.assignedEvents ?? []
            if (assigned.length === 0) {
              setEventsLoading(false)
              return
            }
            Promise.all(assigned.map((id) => adminEventsService.getEventById(id))).then(
              (results) => {
                setEvents(results.filter((e): e is EventDocument & { id: string } => e !== null))
                setEventsLoading(false)
              },
            )
          }
        })
        .catch(() => setEventsLoading(false))
  }, [user])

  // Subscribe to real-time ticket counts when an event is selected
  useEffect(() => {
    if (!selectedEventId) return

    const unsubscribe = adminEventsService.subscribeToTicketCounts(
      selectedEventId,
      (tickets: Array<TicketDocument & { id: string }>) => {
        const countMap = new Map<string, { ticketTypeName: string; total: number; checkedIn: number }>()
        for (const ticket of tickets) {
          const existing = countMap.get(ticket.ticketTypeId)
          if (!existing) {
            countMap.set(ticket.ticketTypeId, {
              ticketTypeName: ticket.ticketTypeName,
              total: 1,
              checkedIn: ticket.checkedIn ? 1 : 0,
            })
          } else {
            existing.total += 1
            if (ticket.checkedIn) existing.checkedIn += 1
          }
        }
        setTicketCounts(
          Array.from(countMap.entries()).map(([ticketTypeId, data]) => ({
            ticketTypeId,
            ...data,
          })),
        )
      },
    )

    return unsubscribe
  }, [selectedEventId])

  const selectedEvent = events.find((e) => e.id === selectedEventId)
  const scannerActive = state === 'scanning' || state === 'loading'

  function handleScan(qrToken: string) {
    if (state === 'loading') return
    void checkIn(qrToken)
  }

  if (eventsLoading) {
    return (
      <div className="container mx-auto max-w-xl px-4 py-12 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-xl px-4 py-12 space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Check-In</h1>
        <p className="mt-1 text-muted-foreground">
          Select an event and scan attendee QR codes.
        </p>
      </div>

      {/* Event selector */}
      {!selectedEventId ? (
        <div className="space-y-3">
          <p className="text-sm font-medium">Select an Event</p>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {isAdmin
                ? 'No events found.'
                : "You haven't been assigned to any events yet."}
            </p>
          ) : (
            events.map((event) => (
              <button
                key={event.id}
                type="button"
                onClick={() => setSelectedEventId(event.id)}
                className="w-full rounded-lg border bg-card p-4 text-left hover:bg-muted/50 transition-colors"
              >
                <p className="font-medium">{event.title}</p>
                <p className="text-sm text-muted-foreground">
                  {event.eventDate.toDate().toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}{' '}
                  · {event.venue}
                </p>
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold">{selectedEvent?.title}</p>
              <p className="text-sm text-muted-foreground">{selectedEvent?.venue}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedEventId(null)
                setTicketCounts([])
                reset()
              }}
            >
              Change Event
            </Button>
          </div>

          {/* Live check-in counts */}
          {ticketCounts.length > 0 && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4" />
                Live Attendance
              </div>
              {ticketCounts.map((count) => (
                <div
                  key={count.ticketTypeId}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-muted-foreground">{count.ticketTypeName}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {count.checkedIn} / {count.total}
                    </Badge>
                    <span className="text-xs text-muted-foreground">checked in</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Result overlay */}
          {(state === 'success' || state === 'error') && (
            <CheckInResult
              state={state}
              result={result}
              errorMsg={errorMsg}
              onReset={() => {
                reset()
                startScanning()
              }}
            />
          )}

          {/* Scanner */}
          {state === 'idle' && (
            <Button className="w-full" size="lg" onClick={startScanning}>
              <ScanLine className="mr-2 h-5 w-5" />
              Start Scanning
            </Button>
          )}

          {state === 'loading' && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">Validating ticket…</p>
            </div>
          )}

          {scannerActive && (
            <QrScanner onScan={handleScan} active={scannerActive} />
          )}
        </div>
      )}
    </div>
  )
}
